import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { SITE_URL } from '../../config/productFacts';
import CITIES from '../../data/tankstellenCities.json';
import { fmtPrice } from '../tankstellen/tankstellenShared';

const API = process.env.REACT_APP_BACKEND_URL;

export const TOP100 = [...CITIES].filter((c) => c.rank <= 100).sort((a, b) => a.rank - b.rank);

const FUEL_PREF_KEY = 'fuelx_fuel_pref';
export function getFuelPref() {
  try {
    const v = localStorage.getItem(FUEL_PREF_KEY);
    return ['e5', 'e10', 'diesel'].includes(v) ? v : null;
  } catch {
    return null;
  }
}
export function setFuelPref(v) {
  try {
    localStorage.setItem(FUEL_PREF_KEY, v);
  } catch {
    /* Privat-Modus */
  }
}

/** Live-DE-Durchschnittspreise (Tankerkönig-Stats, Backend-Cache 10 Min) */
export function useNationalPrices() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/fuel-prices`).then((res) => { if (active) setData(res.data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  return data;
}

export function breadcrumbSchema(parts) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: parts.map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${SITE_URL}${path}`,
    })),
  };
}

export function faqSchema(items) {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

/** Seitenkopf: Breadcrumb + Eyebrow + H1 + Intro */
export function ClusterHero({ crumbs, eyebrow, h1, intro, testId = 'cluster' }) {
  return (
    <>
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[12px] text-stone-400 mb-8" data-testid={`${testId}-breadcrumb`}>
        {crumbs.map(([label, to], i) => (
          <React.Fragment key={label}>
            {i > 0 && <ChevronRight className="w-3 h-3" strokeWidth={2} />}
            {to ? (
              <Link to={to} className="hover:text-stone-900 transition-colors">{label}</Link>
            ) : (
              <span className="text-stone-700">{label}</span>
            )}
          </React.Fragment>
        ))}
      </nav>
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-700 mb-5">{eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.03em] text-stone-900" data-testid={`${testId}-h1`}>
        {h1}
      </h1>
      <p className="text-lg text-stone-600 leading-[1.7] mt-6 max-w-2xl" data-testid={`${testId}-intro`}>{intro}</p>
    </>
  );
}

/** 3 DE-Ø-Preiskarten (E5 / E10 / Diesel) — highlight hebt Sorten dunkel hervor */
export function NationalPriceCards({ national, highlight = [], testId = 'national-prices' }) {
  const prices = national?.prices || {};
  const cards = [['e5', 'Super E5'], ['e10', 'Super E10'], ['diesel', 'Diesel']];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10" data-testid={testId}>
      {cards.map(([key, label]) => {
        const active = !highlight.length || highlight.includes(key);
        return (
          <div
            key={key}
            className={`rounded-2xl border p-5 ${active ? 'border-stone-900 bg-stone-950 text-white' : 'border-stone-200 bg-white'}`}
            data-testid={`${testId}-${key}`}
          >
            <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${active ? 'text-emerald-400' : 'text-stone-400'}`}>
              {label} · DE-Ø heute
            </p>
            <p className={`mt-2 text-3xl font-medium tabular-nums tracking-tight ${active ? 'text-white' : 'text-stone-900'}`}>
              {national ? fmtPrice(prices[key]) : '···'}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/** Städte-Linkwolke auf Cluster-Stadtseiten (Top-100) */
export function CityLinkCloud({ base, title, subtitle, count = 100, testIdPrefix = 'city-cloud' }) {
  const cities = TOP100.slice(0, count);
  return (
    <div className="mt-14" data-testid={`${testIdPrefix}-section`}>
      <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-2">{title}</h2>
      {subtitle && <p className="text-[14px] text-stone-500">{subtitle}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-6">
        {cities.map((c) => (
          <Link
            key={c.slug}
            to={`${base}/${c.slug}`}
            className="text-[13.5px] text-stone-600 hover:text-emerald-700 hover:underline underline-offset-2 transition-colors"
            data-testid={`${testIdPrefix}-${c.slug}`}
          >
            {c.name}
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Vollständiges Städte-Verzeichnis (500) gruppiert nach Bundesland */
export function CityDirectory({ base, title, subtitle, testIdPrefix = 'city-dir' }) {
  const byState = {};
  for (const c of CITIES) {
    const key = c.bundesland || 'Weitere Städte';
    (byState[key] = byState[key] || []).push(c);
  }
  const groups = Object.entries(byState)
    .map(([state, cities]) => [state, [...cities].sort((a, b) => a.name.localeCompare(b.name, 'de'))])
    .sort((a, b) => a[0].localeCompare(b[0], 'de'));
  return (
    <div className="mt-14" data-testid={`${testIdPrefix}-section`}>
      <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-2">{title}</h2>
      {subtitle && <p className="text-[14px] text-stone-500 mb-8">{subtitle}</p>}
      <div className="space-y-8">
        {groups.map(([state, cities]) => (
          <div key={state}>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400 mb-3">
              {state} <span className="normal-case tracking-normal">({cities.length})</span>
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`${base}/${c.slug}`}
                  className="text-[13.5px] text-stone-600 hover:text-emerald-700 hover:underline underline-offset-2 transition-colors"
                  data-testid={`${testIdPrefix}-${c.slug}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** FAQ-Abschnitt (Fragen aus [frage, antwort]-Paaren — synchron zum FAQPage-Schema) */
export function FaqSection({ title, items, testId = 'faq-section' }) {
  return (
    <section className="mt-14" data-testid={testId}>
      <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-5">{title}</h2>
      <div className="space-y-5 text-[15.5px] text-stone-600 leading-[1.75]">
        {items.map(([q, a]) => (
          <div key={q}>
            <h3 className="font-semibold text-stone-900 mb-1.5">{q}</h3>
            <p>{a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Interne Querverlinkung als Chip-Reihe */
export function CrossLinkChips({ title = 'Weiterlesen & vergleichen', links, testId = 'cross-links' }) {
  return (
    <div className="mt-14 border-t border-stone-200 pt-8" data-testid={testId}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-400 mb-4">{title}</p>
      <div className="flex flex-wrap gap-2.5">
        {links.map(([label, to]) => (
          <Link
            key={to + label}
            to={to}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-[13px] font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors"
          >
            {label} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        ))}
      </div>
    </div>
  );
}

/** FuelX-Vorteilsbox (identisch zum Ratgeber-Stil) */
export function FuelXBox({ title, children, linkTo = '/fuelx-card', linkLabel = 'FuelX Card entdecken', testId = 'fuelx-box' }) {
  return (
    <div className="mt-14 rounded-2xl bg-emerald-50 border border-emerald-200 p-6 sm:p-7" data-testid={testId}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700 mb-2.5">So hilft FuelX</p>
      <h3 className="font-display text-xl tracking-[-0.015em] text-stone-900 mb-3">{title}</h3>
      <div className="text-[15px] text-stone-700 leading-[1.7]">{children}</div>
      <Link to={linkTo} className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
        {linkLabel} <ArrowRight className="w-4 h-4" strokeWidth={2} />
      </Link>
    </div>
  );
}
